import React, { useEffect, useState } from "react";
import { Card, Button, Table, Form, Alert, Pagination, InputGroup } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { IconSearch } from "@tabler/icons-react";
import StatusFilterBar from "../components/StatusFilterBar";
import { Helmet } from "react-helmet-async";

const AllTasks = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [urls, setUrls] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("creation_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [urlsPerPage, setUrlsPerPage] = useState(50);
  const [summary, setSummary] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const navigate = useNavigate();
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [user, setUser] = useState(null);
  const [immediateCheckLoading, setImmediateCheckLoading] = useState(false);

  const isAdmin = user?.role === "admin";

  const fetchUrls = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let url = `/api/admin/all-urls?limit=${urlsPerPage}&skip=${
        (currentPage - 1) * urlsPerPage
      }&ordering=${sortBy}&direction=${sortOrder}`;

      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      if (activeFilter !== null) {
        url += `&status=${encodeURIComponent(activeFilter)}`;
      }
      if (activeFilter !== null) {
        url += `&status=${encodeURIComponent(activeFilter)}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Ошибка загрузки данных");
      const data = await response.json();
      setUrls(data.items);
      setSummary(data.summary || {});
      setTotalCount(data.total_count || 0);
      setTotalPages(
        Math.max(1, Math.ceil((data.total_count || 0) / urlsPerPage))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUrls();
  }, [currentPage, sortBy, sortOrder, urlsPerPage, activeFilter, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeFilter]);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/auth/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setUser(data);
    };

    fetchUser();
  }, []);

  const getPaginationPages = (current, total) => {
    const delta = 2;
    const pages = new Set([1, total]);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      result.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1)
        result.push("...");
    }
    return result;
  };

  const restartTasks = async () => {
    if (!window.confirm("Перезапустить задачи?")) return;
    const token = localStorage.getItem("token");
    await fetch(`/api/admin/restart-indexing`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchUrls();
  };

  // НОВАЯ ФУНКЦИЯ: разовая немедленная проверка Request in progress
  const runImmediateIndexCheck = async () => {
    if (
      !window.confirm(
        "Запустить разовую немедленную проверку всех ссылок со статусом Request in progress?"
      )
    )
      return;

    try {
      setImmediateCheckLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch("/api/admin//force-index-check", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        let msg = "Ошибка запуска проверки";
        try {
          const errJson = await res.json();
          if (errJson?.detail) msg = errJson.detail;
        } catch (e) {
          // ignore json error
        }
        throw new Error(msg);
      }

      alert("Задача немедленной проверки успешно запущена.");
      fetchUrls();
    } catch (err) {
      console.error(err);
      alert("Не удалось запустить немедленную проверку: " + err.message);
    } finally {
      setImmediateCheckLoading(false);
    }
  };

  const handleDeleteOlderThan3Months = async () => {
    if (!window.confirm("Удалить все ссылки старше 3 месяцев?")) return;
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        "/api/admin/delete-urls-older-than-3-months",
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Ошибка удаления");
      alert("Старые ссылки удалены");
      fetchUrls();
    } catch (err) {
      alert("Ошибка: " + err.message);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedUrls.length === 0) return;
    if (!window.confirm("Удалить выбранные ссылки?")) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/admin/delete-links`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ link_ids: selectedUrls }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Ошибка при удалении");
      }

      setSelectedUrls([]);
      fetchUrls();
    } catch (err) {
      console.error("Ошибка:", err);
      alert("Не удалось удалить ссылки: " + err.message);
    }
  };

  const renderSortHeader = (label, field) => (
    <th
      style={{ cursor: "pointer" }}
      onClick={() => {
        if (sortBy === field)
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        else {
          setSortBy(field);
          setSortOrder("asc");
        }
      }}
    >
      {label} {sortBy === field && (sortOrder === "asc" ? "▲" : "▼")}
    </th>
  );

  return (
    <>
      <Helmet>
        <title>All Tasks - IndexBooster</title>
      </Helmet>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h3 className="card-title m-0">All Tasks</h3>
          {isAdmin && (
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={restartTasks}>
                Перезапустить задачи
              </Button>
              <Button
                variant="outline-primary"
                onClick={runImmediateIndexCheck}
                disabled={immediateCheckLoading}
              >
                {immediateCheckLoading
                  ? "Запуск проверки..."
                  : "Разовая проверка сейчас"}
              </Button>
            </div>
          )}
        </Card.Header>

        <Card.Body className="border-bottom py-3">
          <StatusFilterBar
            summary={summary}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
          />
          <div className="d-flex justify-content-start">
            <InputGroup style={{ maxWidth: 300 }}>
              <InputGroup.Text>
                <IconSearch size={18} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by URL"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </div>
        </Card.Body>

        <div className="table-responsive">
          <Table className="table card-table table-vcenter text-nowrap datatable">
            <thead>
              <tr>
                <th className="w-1">
                  <Form.Check
                    type="checkbox"
                    checked={
                      selectedUrls.length > 0 &&
                      selectedUrls.length === urls.length
                    }
                    onChange={(e) =>
                      setSelectedUrls(
                        e.target.checked ? urls.map((u) => u.id) : []
                      )
                    }
                  />
                </th>
                {renderSortHeader("URL", "url")}
                {renderSortHeader("Indexable?", "indexing_status")}
                {renderSortHeader("Created", "creation_date")}
                {renderSortHeader("Indexing Status", "index_google_status")}

                <th>User</th>
                <th>Project</th>
                <th>Snippet Info</th>
              </tr>
            </thead>
            <tbody>
              {error && <Alert variant="danger">{error}</Alert>}
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <div
                      className="spinner-border text-primary"
                      style={{ width: "3rem", height: "3rem" }}
                      role="status"
                    >
                      <span className="visually-hidden">Загрузка...</span>
                    </div>
                    <div className="mt-2 text-muted">Загрузка данных...</div>
                  </td>
                </tr>
              ) : (
                urls.map((url) => (
                  <tr key={url.id}>
                    {isAdmin && (
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedUrls.includes(url.id)}
                          onChange={(e) => {
                            setSelectedUrls((prev) =>
                              e.target.checked
                                ? [...prev, url.id]
                                : prev.filter((id) => id !== url.id)
                            );
                          }}
                        />
                      </td>
                    )}

                    <td>{url.url}</td>
                    <td className="text-center">
                      {url.indexing_status?.startsWith("YES") ? (
                        <span className="text-success">
                          {url.indexing_status}
                        </span>
                      ) : url.indexing_status?.startsWith("NO") ? (
                        <span className="text-danger">
                          {url.indexing_status}
                        </span>
                      ) : (
                        <span className="text-warning">Pending...</span>
                      )}
                    </td>
                    <td>{new Date(url.creation_date).toLocaleString()}</td>
                    <td className="text-center" style={{ minWidth: "220px" }}>
                      {url.index_google_status === "Request in progress" ? (
                        <div className="alert alert-info py-1 px-2 mb-0 w-100">
                          Request in progress
                        </div>
                      ) : url.index_google_status ===
                        "Non indexable (refunded)" ? (
                        <div className="alert alert-danger py-1 px-2 mb-0 w-100">
                          Non indexable (refunded)
                        </div>
                      ) : url.index_google_status ===
                        "Non indexed (refunded)" ? (
                        <div className="alert alert-danger py-1 px-2 mb-0 w-100">
                          Non indexed (refunded)
                        </div>
                      ) : url.index_google_status === "Indexed" ? (
                        <div className="alert alert-success py-1 px-2 mb-0 w-100">
                          Indexed
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {url.user ? (
                        <Link
                          to={`/user/${url.user.id}/projects`}
                          className="text-primary"
                        >
                          {url.user.email}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {url.project ? (
                        <Link
                          to={`/project/${url.project.id}`}
                          className="text-primary"
                        >
                          {url.project.name}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      style={{
                        maxWidth: "400px",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        textAlign: "left",
                      }}
                    >
                      {url.index_google_status === "Indexed" &&
                      url.snippet_title ? (
                        <div>
                          <div
                            style={{
                              color: "#1a0dab",
                              fontWeight: 500,
                            }}
                          >
                            {url.snippet_title}
                          </div>
                          <div style={{ color: "#000" }}>
                            {url.snippet_description}
                          </div>
                        </div>
                      ) : url.index_google_status ===
                          "Non indexable (refunded)" &&
                        url.snippet_info ===
                          "Попробуйте добавить страницу на индексацию позже" ? (
                        <div style={{ color: "#555" }}>
                          Indexing not possible, please try again later
                        </div>
                      ) : url.snippet_info ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: url.snippet_info,
                          }}
                        />
                      ) : (
                        "Ожидание проверки"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        <Card.Footer className="px-4">
          <div className="d-flex flex-column gap-2 w-100">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 w-100">
              <div className="d-flex align-items-center flex-wrap gap-3">
                <Pagination className="m-0 flex-wrap">
                  <Pagination.Prev
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    «
                  </Pagination.Prev>
                  {getPaginationPages(currentPage, totalPages).map(
                    (page, i) =>
                      page === "..." ? (
                        <Pagination.Ellipsis key={`ellipsis-${i}`} disabled />
                      ) : (
                        <Pagination.Item
                          key={`page-${page}`}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      )
                  )}
                  <Pagination.Next
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    »
                  </Pagination.Next>
                </Pagination>

                <div className="text-muted">
                  Showing <b>{(currentPage - 1) * urlsPerPage + 1}</b>–<b>
                    {Math.min(currentPage * urlsPerPage, totalCount)}
                  </b>{" "}
                  of <b>{totalCount}</b> entries
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 ms-md-auto">
                <span className="text-muted">Show</span>
                <Form.Select
                  value={urlsPerPage}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setUrlsPerPage(Number(e.target.value));
                  }}
                  size="sm"
                >
                  {[10, 25, 50, 100, 200, 500].map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </Form.Select>
                <span className="text-muted">entries</span>
              </div>
            </div>

            <div className="d-flex justify-content-start gap-2">
              <Button
                variant="danger"
                disabled={selectedUrls.length === 0}
                onClick={handleDeleteSelected}
              >
                Delete Selected
              </Button>
              <Button
                variant="outline-danger"
                onClick={handleDeleteOlderThan3Months}
              >
                Delete older than 3 months
              </Button>
            </div>
          </div>
        </Card.Footer>
      </Card>
    </>
  );
};

export default AllTasks;
